import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AiFillHome, AiOutlineSearch, AiOutlineMail, AiOutlineUser, AiOutlineMore } from 'react-icons/ai';
import { IoNotificationsOutline } from 'react-icons/io5';
import { BsBookmark } from 'react-icons/bs';
import { MdFormatListBulleted } from 'react-icons/md';
import { FiShoppingCart } from 'react-icons/fi';
import { HiUserAdd } from 'react-icons/hi';
import { BiLogOut } from 'react-icons/bi';
import { MdDarkMode, MdLightMode, MdBarChart, MdDashboard, MdCreate, MdAccessTime, MdEventNote } from 'react-icons/md';
import { RiAdvertisementLine } from 'react-icons/ri';
import { BsPersonBadge } from 'react-icons/bs';
import { MdExpandMore, MdExpandLess, MdInfo } from 'react-icons/md';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSocket } from '../contexts/SocketContext';
import { useNotification } from '../contexts/NotificationContext';
import PostModal from './PostModal';
import { apiService, safeApiCall } from '../utils/api';
import { normalizeImageUrl } from './Post';
import config from '../config';
import { ROLES } from '../constants/roles';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';

import './Sidebar.css';

// ユーザープロフィール取得をキャッシュするためのマップ
const userProfileCache = new Map();
const USER_PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5分間キャッシュを有効にする

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, currentUser, logout, checkPermission, token, userRole, isInitialized } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { connected, on, emit } = useSocket();
  const { unreadCount, unreadMessageCount, fetchUnreadCounts } = useNotification();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const menuRef = useRef(null);
  const [userProfile, setUserProfile] = useState(null);
  // レスポンシブ対応のための状態を追加
  const [isCompact, setIsCompact] = useState(window.innerWidth <= 1200);
  // モバイル表示かどうかの状態を追加
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // 初回のデータロードが完了したかを追跡
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // ダッシュボードメニューの表示/非表示状態
  const [showDashboardMenu, setShowDashboardMenu] = useState(false);
  // その他の情報メニューの表示/非表示状態
  const [showInfoMenu, setShowInfoMenu] = useState(false);
  
  // 企業ユーザーかどうかのチェック（より確実に）
  const isCorporateUser = React.useMemo(() => {
    const roleCheck = userRole === ROLES.CORPORATE;
    const currentUserRoleCheck = currentUser && currentUser.role === 'corporate';
    
    console.log('【サイドバー】企業ユーザーチェック詳細:', {
      userRole,
      'ROLES.CORPORATE': ROLES.CORPORATE,
      roleCheck,
      currentUserRoleCheck,
      'currentUser?.role': currentUser?.role,
      '最終結果': roleCheck || currentUserRoleCheck
    });
    
    return roleCheck || currentUserRoleCheck;
  }, [userRole, currentUser]);


  
  // ダッシュボード関連のページであるかどうかをチェック
  const isDashboardRelatedPage = location.pathname.startsWith('/corporate/');
  
  // 投稿ユーザーかどうかのチェック（より確実に）
  const isPosterUser = React.useMemo(() => {
    const roleCheck = userRole === ROLES.POSTER;
    const currentUserRoleCheck = currentUser && currentUser.role === 'poster';
    
    console.log('【サイドバー】投稿ユーザーチェック詳細:', {
      userRole,
      'ROLES.POSTER': ROLES.POSTER,
      roleCheck,
      currentUserRoleCheck,
      'currentUser?.role': currentUser?.role,
      '最終結果': roleCheck || currentUserRoleCheck
    });
    
    return roleCheck || currentUserRoleCheck;
  }, [userRole, currentUser]);

  // 管理者ユーザーかどうかのチェック
  const isAdminUser = React.useMemo(() => {
    const roleCheck = userRole === ROLES.ADMIN;
    const currentUserRoleCheck = currentUser && currentUser.role === 'admin';
    
    console.log('【サイドバー】管理者ユーザーチェック詳細:', {
      userRole,
      'ROLES.ADMIN': ROLES.ADMIN,
      roleCheck,
      currentUserRoleCheck,
      'currentUser?.role': currentUser?.role,
      '最終結果': roleCheck || currentUserRoleCheck
    });
    
    return roleCheck || currentUserRoleCheck;
  }, [userRole, currentUser]);

  // ウィンドウサイズ変更を検知するイベントリスナー
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsCompact(width <= 1200);
      setIsMobile(width <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // 初期値を設定
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 初回ログイン時に未読カウントを取得（リアルタイム更新システムが引き継ぎ）
  useEffect(() => {
    if (isLoggedIn && fetchUnreadCounts) {
      fetchUnreadCounts();
      
      // リアルタイム更新システムにより定期的ポーリングは不要
      // EventSourceまたはスマートポーリングが自動で未読カウントを更新
    }
  }, [isLoggedIn, fetchUnreadCounts]);

  // ユーザープロフィール取得（TweetBoxと同じ方法）
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (isLoggedIn && isInitialized) {
        try {
          const response = await safeApiCall.get('/api/users/me');
          console.log('Sidebar: ユーザープロファイル取得成功:', response.data);
          setUserProfile(response.data);
        } catch (err) {
          console.error('Sidebar: ユーザープロファイル取得エラー:', err);
          // 401エラーの場合でも、現在のユーザー情報を使用することで機能を維持する
          if (err?.response?.status === 401 && currentUser) {
            console.log('Sidebar: currentUserをフォールバックとして使用:', currentUser);
            setUserProfile(currentUser);
          }
        }
      } else if (isLoggedIn && currentUser) {
        // 初期化が完了していない場合は、currentUserをそのまま使用
        console.log('Sidebar: 初期化未完了のため currentUser を使用:', currentUser);
        setUserProfile(currentUser);
      } else {
        console.log('Sidebar: ユーザープロファイル設定条件不適合:', { isLoggedIn, currentUser, isInitialized });
      }
    };
    
    fetchUserProfile();
  }, [isLoggedIn, currentUser, isInitialized]);

  // デバッグ用：userProfileとcurrentUserの変更を監視
  useEffect(() => {
    console.log('Sidebar: userProfile変更:', {
      userProfile,
      avatar: userProfile?.avatar,
      currentUserAvatar: currentUser?.avatar
    });
  }, [userProfile, currentUser]);

  // 現在のページかどうかをチェックする関数
  const isCurrentPage = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // サイドバー下部に表示するユーザー名を解決する。
  // ウィジェットの「おすすめユーザー」と同じ方針:
  // 投稿ユーザーは「芸名＠店舗名」、それ以外は 表示名 → 芸名 → ユーザー名 の順で表示。
  // （投稿ユーザーは display_name が空でユーザー名にフォールバックしてしまう問題への対応）
  const getSidebarDisplayName = () => {
    const u = userProfile || currentUser || {};
    const stageName = u.stage_name || u.stageName;
    const shopName = u.shop_name || u.shopName;
    if (stageName && shopName) return `${stageName}＠${shopName}`;
    return u.display_name || u.displayName || stageName || u.username || 'ユーザー';
  };
  
  // ログアウト処理
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // もっと見るメニューを開く
  const handleMoreMenuOpen = (event) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  // もっと見るメニューを閉じる
  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  // ユーザーメニューの表示/非表示を切り替える
  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  // ページに移動する
  const goToPage = (path) => {
    navigate(path);
    handleMoreMenuClose();
  };

  // 投稿モーダルを開く
  const handlePostClick = () => {
    setShowPostModal(true);
  };

  // ダッシュボードメニューの表示/非表示を切り替える
  const toggleDashboardMenu = () => {
    setShowDashboardMenu(!showDashboardMenu);
  };

  // メニュー項目のフィルタリングを安全に行う関数
  const safeFilterItems = (items) => {
    if (!Array.isArray(items)) return [];
    return items.filter(item => item && item.visible);
  };

  // モバイル表示用のメニュー項目を取得する関数
  const getMobileMenuItems = () => {
    // モバイル表示では基本的なメニューのみを表示し、その他は三点リーダーに格納
    const mobileItems = [
      {
        text: 'ホーム',
        icon: <img src="/images/DNS.svg" alt="SNS Deli" style={{ width: '24px', height: '24px' }} />,
        path: '/',
        visible: true
      },
      {
        text: '検索',
        icon: <SearchIcon />,
        path: '/explore',
        visible: true
      },
      {
        text: '通知',
        icon: isLoggedIn ? (
          <Badge badgeContent={unreadCount} color="primary">
            <NotificationsNoneIcon />
          </Badge>
        ) : (
          <NotificationsNoneIcon />
        ),
        path: '/notifications',
        visible: isLoggedIn
      },
      {
        text: 'メッセージ',
        icon: isLoggedIn ? (
          <Badge badgeContent={unreadMessageCount} color="primary">
            <MailOutlineIcon />
          </Badge>
        ) : (
          <MailOutlineIcon />
        ),
        path: '/messages',
        visible: isLoggedIn
      },
      {
        text: 'もっと見る',
        icon: <MoreHorizIcon />,
        action: handleMoreMenuOpen,
        visible: true,
        isButton: true
      }
    ];

    return mobileItems || [];
  };

  // 三点リーダーメニューの項目を権限ごとに取得する関数
  const getMoreMenuItems = () => {
    const moreItems = [];

    // 共通項目（ログイン時）
    if (isLoggedIn) {
      moreItems.push(
        {
          text: 'ブックマーク',
          icon: <BookmarkBorderIcon fontSize="small" />,
          path: '/bookmarks'
        },
        {
          text: 'リスト',
          icon: <ListAltIcon fontSize="small" />,
          path: '/lists'
        },
        {
          text: 'ランキング',
          icon: <EmojiEventsIcon fontSize="small" />,
          path: '/rankings'
        },
        {
          text: 'クチコミランキング',
          icon: <StarIcon fontSize="small" />,
          path: '/rankings?tab=reviews'
        },
        {
          text: 'プロフィール',
          icon: <PermIdentityIcon fontSize="small" />,
          path: `/profile/${currentUser?.username}`
        }
      );
    }

    // ゲストユーザー用の項目
    if (!isLoggedIn) {
      moreItems.push(
        {
          text: 'ランキング',
          icon: <EmojiEventsIcon fontSize="small" />,
          path: '/rankings'
        },
        {
          text: 'クチコミランキング',
          icon: <StarIcon fontSize="small" />,
          path: '/rankings?tab=reviews'
        },
        {
          text: 'プロフィール',
          icon: <PermIdentityIcon fontSize="small" />,
          path: '/login'
        },
        {
          text: 'ログイン',
          icon: <BiLogOut fontSize="small" />,
          path: '/login'
        },
        {
          text: '新規登録',
          icon: <HiUserAdd fontSize="small" />,
          path: '/register'
        }
      );
    }

    // 投稿ユーザー用の項目
    if (isPosterUser) {
      moreItems.push({
        text: 'シフト管理',
        icon: <MdAccessTime fontSize="small" />,
        path: '/shifts'
      });
      moreItems.push({
        text: '予約管理',
        icon: <MdEventNote fontSize="small" />,
        path: '/my-reservations'
      });
    }

    // 企業ユーザー用の項目
    if (isCorporateUser) {
      moreItems.push(
        {
          text: 'ダッシュボード',
          icon: <MdDashboard fontSize="small" />,
          path: '/corporate/dashboard'
        },
        {
          text: '広告管理',
          icon: <RiAdvertisementLine fontSize="small" />,
          path: '/corporate/advertisements'
        },
        {
          text: 'タグ付きユーザー',
          icon: <BsPersonBadge fontSize="small" />,
          path: '/corporate/tagged-users'
        },
        {
          text: '予約管理',
          icon: <MdEventNote fontSize="small" />,
          path: '/corporate/reservations'
        },
        {
          text: '企業プロフィール',
          icon: <AiOutlineUser fontSize="small" />,
          path: '/corporate/profile'
        }
      );
    }

    // その他の情報メニュー
    moreItems.push({
      text: 'その他の情報',
      icon: <MdInfo fontSize="small" />,
      expandIcon: showInfoMenu ? <MdExpandLess fontSize="small" /> : <MdExpandMore fontSize="small" />,
      action: () => setShowInfoMenu(!showInfoMenu),
      isExpandable: true,
      expanded: showInfoMenu,
      subItems: [
        {
          text: '広告掲載について',
          path: '/ads'
        },
        {
          text: '利用規約',
          path: '/terms'
        },
        {
          text: 'プライバシーポリシー',
          path: '/privacy'
        },
        {
          text: 'Cookie',
          path: '/cookies'
        }
      ]
    });

    // ダークモード切り替え
    moreItems.push({
      text: isDarkMode ? 'ライトモード' : 'ダークモード',
      icon: isDarkMode ? <MdLightMode fontSize="small" /> : <MdDarkMode fontSize="small" />,
      action: toggleTheme
    });

    // 通知・設定（ログイン時のみ）
    if (isLoggedIn) {
      moreItems.push({
        text: '通知・設定',
        icon: <IoNotificationsOutline fontSize="small" />,
        path: '/settings'
      });
    }

    // ログアウト（ログイン時のみ）
    if (isLoggedIn) {
      moreItems.push({
        text: 'ログアウト',
        icon: <BiLogOut fontSize="small" />,
        action: handleLogout
      });
    }

    return moreItems;
  };

  const getMenuItems = () => {
    // 基本メニュー項目
    const baseItems = [
      {
        text: 'ホーム',
        icon: <HomeIcon />,
        path: '/',
        visible: true
      },
      {
        text: '検索',
        icon: <SearchIcon />,
        path: '/explore',
        visible: true
      },
      {
        text: '通知',
        icon: isLoggedIn ? (
          <Badge badgeContent={unreadCount} color="primary">
            <NotificationsNoneIcon />
          </Badge>
        ) : (
          <NotificationsNoneIcon />
        ),
        path: '/notifications',
        visible: isLoggedIn
      },
      {
        text: 'メッセージ',
        icon: isLoggedIn ? (
          <Badge badgeContent={unreadMessageCount} color="primary">
            <MailOutlineIcon />
          </Badge>
        ) : (
          <MailOutlineIcon />
        ),
        path: '/messages',
        visible: isLoggedIn
      },
      {
        text: 'ブックマーク',
        icon: <BookmarkBorderIcon />,
        path: '/bookmarks',
        visible: isLoggedIn
      },
      {
        text: 'リスト',
        icon: <ListAltIcon />,
        path: '/lists',
        visible: isLoggedIn
      },
      {
        text: 'プロフィール',
        icon: <PermIdentityIcon />,
        path: isLoggedIn ? `/profile/${currentUser?.username}` : '/login',
        visible: true
      }
    ];
    
    // ゲストユーザー用のメニュー項目
    if (!isLoggedIn) {
      return [
        ...baseItems.filter(item => item.visible),
        {
          text: 'ログイン',
          icon: <BiLogOut />,
          path: '/login',
          visible: true
        },
        {
          text: '新規登録',
          icon: <HiUserAdd />,
          path: '/register',
          visible: true
        }
      ];
    }
    
    // 投稿ユーザー用のシフト管理メニューを追加（管理者でない場合のみ）
    if (isPosterUser && !isAdminUser) {
      baseItems.push({
        text: 'シフト管理',
        icon: <MdAccessTime />,
        path: '/shifts',
        visible: true
      });
      baseItems.push({
        text: '予約管理',
        icon: <MdEventNote />,
        path: '/my-reservations',
        visible: true
      });
    }
    
    // 管理者用のメニュー項目を追加
    if (isAdminUser) {
      baseItems.push({
        text: '管理者ダッシュボード',
        icon: <MdDashboard />,
        path: '/dashboard',
        visible: true
      });
    }
    
    // 企業ユーザー用のメニュー項目を追加（管理者でない場合のみ）
    if (isCorporateUser && !isAdminUser) {
      baseItems.push({
          text: '企業ダッシュボード',
          icon: showDashboardMenu ? <MdExpandLess /> : <MdExpandMore />,
          action: toggleDashboardMenu,
          visible: true,
          isButton: true
      });
    }
    
    // ログイン中のユーザーには基本メニューをすべて表示
    return baseItems.filter(item => item.visible) || [];
  };
  
  // 企業ダッシュボードのサブメニュー項目
  const dashboardMenuItems = [
    {
      text: 'ダッシュボード',
      icon: <MdDashboard />,
      path: '/corporate/dashboard'
    },
    {
      text: '広告管理',
      icon: <RiAdvertisementLine />,
      path: '/corporate/advertisements'
    },
    {
      text: 'タグ付きユーザー',
      icon: <BsPersonBadge />,
      path: '/corporate/tagged-users'
    },
    {
      text: '予約管理',
      icon: <MdEventNote />,
      path: '/corporate/reservations'
    },
    {
      text: '企業プロフィール',
      icon: <AiOutlineUser />,
      path: '/corporate/profile'
    }
  ];

  return (
    <div className={`sidebar ${isCompact ? 'sidebar--compact' : ''}`}>
      <div className="sidebar__header">
        <Link to="/" className="sidebar__logo">
          <div className="sidebar__logo-img-container">
            <img src="/images/DNS.svg" alt="SNS Deli" className="sidebar__logo-img" />
          </div>
        </Link>
      </div>
      
      <div className="sidebar__menu">
        {/* モバイル表示とPC表示で適切なメニュー項目を表示 */}
        {isMobile 
          ? safeFilterItems(getMobileMenuItems())
              .map((item, index) => (
                <React.Fragment key={index}>
                  {item.isButton ? (
                    <div
                      className={`sidebar__menu-item ${isCurrentPage(item.path) ? 'sidebar__menu-item--active' : ''}`}
                      onClick={item.action}
                    >
                      <div className="sidebar__menu-icon">{item.icon}</div>
                      <div className="sidebar__menu-text">{item.text}</div>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`sidebar__menu-item ${isCurrentPage(item.path) ? 'sidebar__menu-item--active' : ''}`}
                    >
                      <div className="sidebar__menu-icon">{item.icon}</div>
                      <div className="sidebar__menu-text">{item.text}</div>
                    </Link>
                  )}
                </React.Fragment>
              ))
          : safeFilterItems(getMenuItems())
              .map((item, index) => (
                <React.Fragment key={index}>
                  {item.isButton ? (
                    <div
                      className={`sidebar__menu-item ${isCurrentPage(item.path) ? 'sidebar__menu-item--active' : ''}`}
                      onClick={item.action}
                    >
                      <div className="sidebar__menu-icon">{item.icon}</div>
                      {!isCompact && <div className="sidebar__menu-text">{item.text}</div>}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`sidebar__menu-item ${isCurrentPage(item.path) ? 'sidebar__menu-item--active' : ''}`}
                    >
                      <div className="sidebar__menu-icon">{item.icon}</div>
                      {!isCompact && <div className="sidebar__menu-text">{item.text}</div>}
                    </Link>
                  )}
                </React.Fragment>
              ))
        }
        
        {/* 企業ダッシュボードのサブメニュー */}
        {!isMobile && isCorporateUser && showDashboardMenu && (
          <div className="sidebar__submenu">
            {dashboardMenuItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                className={`sidebar__submenu-item ${isCurrentPage(item.path) ? 'sidebar__submenu-item--active' : ''}`}
              >
                <div className="sidebar__submenu-icon">{item.icon}</div>
                {!isCompact && <div className="sidebar__submenu-text">{item.text}</div>}
              </Link>
            ))}
          </div>
        )}
        
        {/* その他のメニュー項目 - モバイル表示では表示しない */}
        {!isMobile && (
          <div className="sidebar__menu-item" onClick={handleMoreMenuOpen}>
            <div className="sidebar__menu-icon"><MoreHorizIcon /></div>
            {!isCompact && <div className="sidebar__menu-text">もっと見る</div>}
          </div>
        )}
        
        {/* Menu */}
        <Menu
          anchorEl={moreMenuAnchor}
          id="account-menu"
          open={Boolean(moreMenuAnchor)}
          onClose={handleMoreMenuClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: isMobile ? -1 : 1.5, // モバイルでは上に表示
              mb: isMobile ? 8 : 0, // モバイルではサイドバー分の余白を確保  
              bgcolor: isDarkMode ? '#15202b' : 'background.paper',
              color: isDarkMode ? '#e7e9ea' : 'inherit',
              zIndex: 2000, // サイドバーより上に表示
              '& .MuiMenuItem-root:hover': {
                bgcolor: isDarkMode ? 'rgba(239, 243, 244, 0.1)' : 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiListItemIcon-root': {
                color: isDarkMode ? '#8899a6' : 'inherit',
              },
              '& .MuiDivider-root': {
                borderColor: isDarkMode ? '#38444d' : 'rgba(0, 0, 0, 0.12)',
              },
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: isMobile ? '100%' : 0, // モバイルでは矢印を下に
                right: 14,
                width: 10,
                height: 10,
                bgcolor: isDarkMode ? '#15202b' : 'background.paper',
                transform: isMobile ? 'translateY(-50%) rotate(-135deg)' : 'translateY(-50%) rotate(45deg)', // モバイルでは矢印を下向きに
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: isMobile ? 'bottom' : 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: isMobile ? 'top' : 'bottom' }}
          slotProps={{
            root: {
              style: {
                zIndex: 2000, // Modalより上に表示
              }
            }
          }}
        >
          {getMoreMenuItems().map((item, index) => (
            <React.Fragment key={index}>
              <MenuItem
                onClick={() => {
                  if (item.isExpandable) {
                    // トグルメニューの場合はメニューを閉じない
                    item.action();
                  } else {
                    if (item.action) {
                      item.action();
                    } else if (item.path) {
                      goToPage(item.path);
                    }
                    handleMoreMenuClose();
                  }
                }}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText>{item.text}</ListItemText>
                {item.isExpandable && (
                  <ListItemIcon>
                    {item.expandIcon}
                  </ListItemIcon>
                )}
              </MenuItem>
              
              {/* サブメニューアイテム */}
              {item.isExpandable && item.expanded && item.subItems && item.subItems.map((subItem, subIndex) => (
                <MenuItem
                  key={`${index}-${subIndex}`}
                  onClick={() => {
                    if (subItem.path) {
                      goToPage(subItem.path);
                    }
                    handleMoreMenuClose();
                  }}
                  sx={{
                    pl: 4, // サブアイテムは左パディングを増やしてインデント
                    bgcolor: isDarkMode ? 'rgba(239, 243, 244, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(239, 243, 244, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  <ListItemText 
                    primary={subItem.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.875rem',
                        color: isDarkMode ? '#8899a6' : 'text.secondary'
                      }
                    }}
                  />
                </MenuItem>
              ))}
            </React.Fragment>
          ))}
        </Menu>
      </div>
      
      {/* 投稿ボタン - モバイル表示では表示しない */}
      {!isMobile && isLoggedIn && checkPermission && checkPermission('POST') && (
        <div className={`sidebar__post-button ${isCompact ? 'sidebar__post-button--compact' : ''}`}>
          <Button
            variant="contained"
            color="primary"
            className="post-button"
            onClick={handlePostClick}
            fullWidth
          >
            {isCompact ? <MdCreate /> : '投稿する'}
          </Button>
        </div>
      )}
      
      {/* ユーザー情報 - モバイル表示では表示しない */}
      {!isMobile && isLoggedIn && (
        <div 
          className="sidebar__user-info" 
          onClick={() => navigate(`/profile/${userProfile?.username || currentUser?.username}`)} 
          ref={menuRef}
          style={{ cursor: 'pointer' }}
        >
          <div className="sidebar__user-avatar">
            <Avatar 
              src={normalizeImageUrl(userProfile?.avatar || currentUser?.avatar)}
              alt={userProfile?.display_name || currentUser?.display_name || 'ユーザー'}
            >
              {(!userProfile?.avatar && !currentUser?.avatar) && 
                (userProfile?.display_name || currentUser?.display_name || userProfile?.username || currentUser?.username || 'U')?.charAt(0)?.toUpperCase()
              }
            </Avatar>
          </div>
          {!isCompact && (
            <div className="sidebar__user-details">
              <div className="sidebar__user-name">{getSidebarDisplayName()}</div>
              <div className="sidebar__user-handle">@{userProfile?.username || currentUser?.username}</div>
            </div>
          )}
        </div>
      )}
      
      {/* Post Modal */}
      {showPostModal && (
        <PostModal 
          open={showPostModal} 
          onClose={() => setShowPostModal(false)} 
        />
      )}
    </div>
  );
}

export default Sidebar; 